import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, Coins, ShoppingCart, CreditCard, FileText, Download, RotateCcw, AlertTriangle, Wallet, BarChart3, Zap, Save, Filter } from "lucide-react";
import { toast } from "sonner";

import api, { formatApiError } from "@/lib/api";
import DashboardLayout from "@/components/DashboardLayout";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { fmtDateTime } from "@/lib/datetime";

const th = "font-display font-bold px-4 py-3 text-xs tracking-wider uppercase text-left text-[#52525B]";
const fld = "h-11 rounded-none border-[#0A0A0A]";
const fmt = (n) => new Intl.NumberFormat("es-MX", { style: "currency", currency: "MXN" }).format(Number(n || 0));
const fmtFecha = (iso) => fmtDateTime(iso);
const METODOS = ["Tarjeta", "Transferencia"];
const ESTADO_STYLE = {
  Aprobada: "text-[#047857] border-[#10B981]/40 bg-[#10B981]/5",
  Pendiente: "text-[#B45309] border-[#F59E0B]/40 bg-[#F59E0B]/5",
  Rechazada: "text-[#B91C1C] border-[#EF4444]/40 bg-[#EF4444]/5",
};

export default function Recargas() {
  const navigate = useNavigate();
  const [paquetes, setPaquetes] = useState([]);
  const [historial, setHistorial] = useState([]);
  const [loading, setLoading] = useState(true);
  const [compra, setCompra] = useState(null); // { paquete, metodo, id_sucursal, referencia }
  const [saving, setSaving] = useState(false);
  const [facturando, setFacturando] = useState(null); // id_recarga en curso
  const [descargando, setDescargando] = useState(null); // `${cfdi_id}-${tipo}`
  const [empresa, setEmpresa] = useState(null);
  const [motivoModal, setMotivoModal] = useState(null); // nota_error a mostrar
  const [reintentando, setReintentando] = useState(null); // cfdi_id

  // Sprint 37.1 — Consumo por sucursal + Auto-recarga global
  const [consumo, setConsumo] = useState(null);
  const [metodos, setMetodos] = useState(null);
  const [rango, setRango] = useState({ desde: "", hasta: "" });
  const [cargandoRango, setCargandoRango] = useState(false);
  const [autoForm, setAutoForm] = useState({ activa: false, umbral: 0, paquete: "" });
  const [guardandoAuto, setGuardandoAuto] = useState(false);

  // Adopción de métodos de captura (% de vehículos registrados por método) — solo analítica
  const [adopcion, setAdopcion] = useState(null);
  const [adopPeriodo, setAdopPeriodo] = useState("hoy"); // hoy | 7d | 30d | rango
  const [adopSucursal, setAdopSucursal] = useState(""); // "" = todas
  const [adopRango, setAdopRango] = useState({ desde: "", hasta: "" });
  const [adopCargando, setAdopCargando] = useState(false);

  const cargarConsumo = useCallback(async (desde = "", hasta = "") => {
    const params = {};
    if (desde && hasta) { params.desde = desde; params.hasta = hasta; }
    const { data } = await api.get("/tenant/mi-empresa/consumo-sucursales", { params });
    setConsumo(data);
    try { const m = await api.get("/tenant/mi-empresa/consumo-metodos", { params }); setMetodos(m.data); } catch { /* noop */ }
  }, []);

  const aplicarRango = async () => {
    if (!rango.desde || !rango.hasta) { toast.error("Selecciona la fecha inicial y final"); return; }
    if (rango.desde > rango.hasta) { toast.error("La fecha inicial no puede ser mayor a la final"); return; }
    setCargandoRango(true);
    try { await cargarConsumo(rango.desde, rango.hasta); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setCargandoRango(false); }
  };

  const limpiarRango = async () => {
    setRango({ desde: "", hasta: "" });
    setCargandoRango(true);
    try { await cargarConsumo(); } finally { setCargandoRango(false); }
  };

  const cargarAdopcion = useCallback(async (periodo, id_sucursal = "", desde = "", hasta = "") => {
    setAdopCargando(true);
    try {
      const params = { periodo };
      if (id_sucursal) params.id_sucursal = id_sucursal;
      if (periodo === "rango" && desde && hasta) { params.desde = desde; params.hasta = hasta; }
      const { data } = await api.get("/tenant/mi-empresa/adopcion-captura", { params });
      setAdopcion(data);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setAdopCargando(false); }
  }, []);

  const cambiarAdopPeriodo = (p) => {
    setAdopPeriodo(p);
    if (p !== "rango") cargarAdopcion(p, adopSucursal);
    else if (adopRango.desde && adopRango.hasta) cargarAdopcion("rango", adopSucursal, adopRango.desde, adopRango.hasta);
  };

  const cambiarAdopSucursal = (sid) => {
    const v = sid === "__all__" ? "" : sid;
    setAdopSucursal(v);
    if (adopPeriodo === "rango") {
      if (adopRango.desde && adopRango.hasta) cargarAdopcion("rango", v, adopRango.desde, adopRango.hasta);
    } else cargarAdopcion(adopPeriodo, v);
  };

  const aplicarAdopRango = () => {
    if (!adopRango.desde || !adopRango.hasta) { toast.error("Selecciona la fecha inicial y final"); return; }
    if (adopRango.desde > adopRango.hasta) { toast.error("La fecha inicial no puede ser mayor a la final"); return; }
    cargarAdopcion("rango", adopSucursal, adopRango.desde, adopRango.hasta);
  };

  const fmtRangoCorto = (iso) => {
    const [, m, d] = iso.split("-").map(Number);
    const meses = ["Ene", "Feb", "Mar", "Abr", "May", "Jun", "Jul", "Ago", "Sep", "Oct", "Nov", "Dic"];
    return `${d} ${meses[m - 1]}`;
  };

  const adopSubtitulo = () => {
    if (adopPeriodo === "hoy") return "Hoy";
    if (adopPeriodo === "7d") return "Últimos 7 días";
    if (adopPeriodo === "30d") return "Últimos 30 días";
    if (adopRango.desde && adopRango.hasta) return `${fmtRangoCorto(adopRango.desde)}–${fmtRangoCorto(adopRango.hasta)}`;
    return "Rango personalizado";
  };

  const guardarAutorecarga = async () => {
    if (autoForm.activa && !autoForm.paquete) { toast.error("Selecciona el paquete de auto-recarga para activarla"); return; }
    setGuardandoAuto(true);
    try {
      const { data } = await api.put("/tenant/mi-empresa/autorecarga", {
        autorecarga_activa: autoForm.activa,
        umbral_folios_alerta: Number(autoForm.umbral) || 0,
        paquete_autorecarga_id: autoForm.paquete || null,
      });
      toast.success("Configuración de auto-recarga global guardada");
      setEmpresa((prev) => prev ? { ...prev, ...data } : prev);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setGuardandoAuto(false); }
  };

  const perfilIncompleto = () => {
    const e = empresa || {};
    return !(e.rfc && e.razon_social && e.regimen_fiscal && e.uso_cfdi_default && e.email_facturacion);
  };

  const reintentarFactura = async (r) => {
    if (perfilIncompleto()) {
      toast.error("Completa tus Datos Fiscales y Correo de Facturación antes de reintentar el CFDI");
      navigate("/app/configuracion-financiera");
      return;
    }
    setReintentando(r.cfdi_id);
    try {
      const { data } = await api.post(`/tenant/cfdi/${r.cfdi_id}/reintentar`);
      toast.success(data.mensaje || "Solicitud reenviada. Recibirás tu factura pronto.");
      load();
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (detail?.code === "PERFIL_FISCAL_INCOMPLETO") {
        toast.error(detail.message);
        navigate("/app/configuracion-financiera");
      } else { toast.error(formatApiError(detail)); }
    }
    finally { setReintentando(null); }
  };

  const solicitarFactura = async (r) => {
    setFacturando(r.id_recarga);
    try {
      const { data } = await api.post(`/tenant/recargas/${r.id_recarga}/facturar`);
      toast.success(data.mensaje || "Solicitud recibida. Recibirás tu factura pronto.");
      load();
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (detail?.code === "PERFIL_FISCAL_INCOMPLETO") {
        toast.error(detail.message || "Completa tus Datos Fiscales y Correo de Facturación antes de solicitar el CFDI");
        navigate("/app/configuracion-financiera");
      } else {
        toast.error(formatApiError(detail));
      }
    }
    finally { setFacturando(null); }
  };

  const descargarCfdi = async (cfdiId, tipo) => {
    setDescargando(`${cfdiId}-${tipo}`);
    try {
      const resp = await api.get(`/tenant/cfdi/${cfdiId}/descargar/${tipo}`, { responseType: "blob" });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `cfdi_${cfdiId}.${tipo}`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (err) { toast.error("No se pudo descargar el archivo."); }
    finally { setDescargando(null); }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [p, h, e] = await Promise.all([api.get("/paquetes"), api.get("/tenant/recargas"), api.get("/mi-empresa")]);
      setPaquetes(p.data); setHistorial(h.data); setEmpresa(e.data);
      setAutoForm({
        activa: !!e.data.autorecarga_activa,
        umbral: e.data.umbral_folios_alerta ?? 0,
        paquete: e.data.paquete_autorecarga_id || "",
      });
      cargarConsumo().catch(() => {});
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setLoading(false); }
  }, [cargarConsumo]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => { cargarAdopcion("hoy", ""); }, [cargarAdopcion]);

  useEffect(() => {
    const status = new URLSearchParams(window.location.search).get("mp_status");
    if (!status) return;
    if (status === "success") toast.success("Pago recibido. Los folios se acreditarán al confirmarse en Mercado Pago.");
    else if (status === "pending") toast.info("Tu pago quedó pendiente en Mercado Pago.");
    else if (status === "failure") toast.error("El pago con Mercado Pago no se completó.");
    window.history.replaceState({}, "", window.location.pathname);
  }, []);

  const abrirCompra = (paquete) => setCompra({ paquete, metodo: "", referencia: "" });
  const esManual = compra && compra.metodo === "Transferencia";

  const confirmar = async () => {
    if (!compra.metodo) { toast.error("Selecciona un método de pago"); return; }
    if (esManual && !compra.referencia.trim()) { toast.error("La referencia del comprobante es obligatoria"); return; }
    setSaving(true);
    try {
      if (compra.metodo === "Tarjeta") {
        // Checkout Pro de Mercado Pago → redirección a init_point.
        const { data } = await api.post("/tenant/recargas/checkout", {
          id_paquete: compra.paquete.id_paquete,
          metodo_pago: "Tarjeta",
        });
        if (data.init_point) {
          toast.success("Redirigiendo a Mercado Pago…");
          window.location.href = data.init_point;
          return;
        }
        throw new Error("No se recibió el enlace de pago");
      }
      // Transferencia → queda Pendiente de validación por el SuperAdmin.
      await api.post("/tenant/recargas", {
        id_paquete: compra.paquete.id_paquete,
        metodo_pago: compra.metodo,
        referencia_comprobante: esManual ? compra.referencia.trim() : null,
      });
      toast.success("Recarga registrada (Pendiente de validación)");
      setCompra(null);
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || err.message); }
    finally { setSaving(false); }
  };

  return (
    <DashboardLayout>
      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}>
        <div className="mb-8">
          <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#52525B] mb-2 flex items-center gap-2"><Coins className="h-4 w-4" /> Configuración</p>
          <h1 className="font-display text-4xl font-black tracking-tight">Recarga de Folios</h1>
          <p className="text-sm text-[#52525B] mt-2">Compra paquetes de folios para tus sucursales.</p>
        </div>

        {/* Sprint 37.1 — Saldo Total Global (bolsa unificada del Tenant) */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-8">
          <div className="lg:col-span-1 border-2 border-[#0A0A0A] bg-[#0A0A0A] text-white p-6 flex flex-col justify-between" data-testid="saldo-global-card">
            <div>
              <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#FFCC00] flex items-center gap-2"><Wallet className="h-4 w-4" /> Saldo Total Global</p>
              <p className="font-display text-5xl font-black mt-4 tracking-tight" data-testid="saldo-global-valor">{empresa ? Number(empresa.saldo_folios || 0).toLocaleString("es-MX") : "—"}</p>
              <p className="text-sm text-[#a1a1aa] mt-1">folios disponibles para todas tus sucursales</p>
            </div>
            {empresa && empresa.umbral_folios_alerta > 0 && empresa.saldo_folios <= empresa.umbral_folios_alerta && (
              <div className="mt-5 border border-[#FFCC00] bg-[#FFCC00]/10 text-[#FFCC00] p-3 flex items-start gap-2 text-sm" data-testid="saldo-alerta-baja">
                <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                <span>Tu saldo está por debajo del umbral de alerta ({Number(empresa.umbral_folios_alerta).toLocaleString("es-MX")}). Recarga pronto.</span>
              </div>
            )}
          </div>

          {/* Auto-recarga global */}
          <div className="lg:col-span-2 border border-[#0A0A0A] bg-white p-6" data-testid="autorecarga-card">
            <div className="flex items-center justify-between mb-4">
              <p className="font-display font-black text-lg tracking-tight flex items-center gap-2"><Zap className="h-4 w-4" /> Auto-recarga Global</p>
              <div className="flex items-center gap-2">
                <span className="text-xs uppercase tracking-wider font-bold text-[#52525B]">{autoForm.activa ? "Activa" : "Inactiva"}</span>
                <Switch checked={autoForm.activa} onCheckedChange={(v) => setAutoForm({ ...autoForm, activa: v })} data-testid="autorecarga-switch" />
              </div>
            </div>
            <p className="text-sm text-[#52525B] mb-4">Cuando el saldo global caiga por debajo del umbral, te avisaremos para recargar el paquete elegido.</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-xs tracking-[0.15em] uppercase font-bold text-[#52525B]">Umbral de alerta (folios)</Label>
                <Input type="number" min={0} value={autoForm.umbral} onChange={(e) => setAutoForm({ ...autoForm, umbral: e.target.value })} data-testid="autorecarga-umbral" className={fld} />
              </div>
              <div className="space-y-2">
                <Label className="text-xs tracking-[0.15em] uppercase font-bold text-[#52525B]">Paquete de auto-recarga</Label>
                <Select value={autoForm.paquete} onValueChange={(v) => setAutoForm({ ...autoForm, paquete: v })}>
                  <SelectTrigger data-testid="autorecarga-paquete" className={fld}><SelectValue placeholder="Selecciona paquete" /></SelectTrigger>
                  <SelectContent>{paquetes.map((p) => <SelectItem key={p.id_paquete} value={p.id_paquete}>{p.nombre} · {p.cantidad_folios.toLocaleString("es-MX")} folios</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex justify-end mt-4">
              <button onClick={guardarAutorecarga} disabled={guardandoAuto} data-testid="autorecarga-guardar" className="btn-hard h-11 px-5 bg-[#FFCC00] text-[#0A0A0A] border border-[#0A0A0A] font-bold uppercase tracking-wider text-sm inline-flex items-center gap-2 disabled:opacity-60">
                {guardandoAuto ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Guardar configuración
              </button>
            </div>
          </div>
        </div>

        {/* Consumo de folios por MÉTODO de registro (Manual 1 · Voz IA 3 · Foto IA 12) */}
        <div className="mb-8" data-testid="consumo-metodo-seccion">
          <h2 className="font-display text-2xl font-black tracking-tight flex items-center gap-2 mb-3"><BarChart3 className="h-5 w-5" /> Consumo de folios por método</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[{ k: "hoy", label: "Hoy" }, { k: "mes", label: "Este mes" }].map(({ k, label }) => {
              const d = metodos?.[k];
              const filas = [
                { id: "MANUAL", nombre: "Manual", peso: 1 },
                { id: "VOZ_IA", nombre: "Voz IA", peso: 3 },
                { id: "FOTO_IA", nombre: "Fotografía IA", peso: 12 },
                { id: "POS", nombre: "POS / Servicios", peso: 1 },
              ];
              return (
                <div key={k} className="border border-[#e4e4e7] bg-white" data-testid={`consumo-metodo-${k}`}>
                  <div className="flex items-center justify-between px-4 py-2.5 border-b border-[#e4e4e7]">
                    <span className="text-xs uppercase tracking-wider font-bold text-[#52525B]">{label}</span>
                    <span className="text-sm font-black text-[#0A0A0A]">{d ? Number(d.total_folios || 0).toLocaleString("es-MX") : "—"} folios</span>
                  </div>
                  <table className="w-full text-sm">
                    <thead><tr className="text-left text-[10px] uppercase tracking-wider text-[#52525B]">
                      <th className="px-4 py-1.5">Método</th><th className="px-4 py-1.5 text-right">Registros</th><th className="px-4 py-1.5 text-right">Folios</th>
                    </tr></thead>
                    <tbody>
                      {filas.map((f) => (
                        <tr key={f.id} className="border-t border-[#f0f0f1]" data-testid={`consumo-metodo-${k}-${f.id}`}>
                          <td className="px-4 py-2">{f.nombre} <span className="text-[#a1a1aa]">· {f.peso}f</span></td>
                          <td className="px-4 py-2 text-right font-mono">{d ? (d[f.id]?.tickets ?? 0) : 0}</td>
                          <td className="px-4 py-2 text-right font-mono font-bold">{d ? (d[f.id]?.folios ?? 0) : 0}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              );
            })}
          </div>
        </div>

        {/* Adopción de métodos de captura — % de VEHÍCULOS registrados por método (solo analítica) */}
        <div className="mb-10" data-testid="adopcion-captura-seccion">
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-3 mb-4">
            <div>
              <h2 className="font-display text-2xl font-black tracking-tight flex items-center gap-2 uppercase"><BarChart3 className="h-5 w-5" /> Adopción de métodos de captura</h2>
              <p className="text-sm text-[#52525B] mt-1" data-testid="adopcion-subtitulo">
                {adopSubtitulo()}{adopSucursal ? ` · ${(consumo?.items || []).find((s) => s.id_sucursal === adopSucursal)?.nombre || ""}` : ""}
              </p>
            </div>
            <div className="space-y-1">
              <Label className="text-[10px] tracking-[0.15em] uppercase font-bold text-[#52525B]">Sucursal</Label>
              <Select value={adopSucursal || "__all__"} onValueChange={cambiarAdopSucursal}>
                <SelectTrigger data-testid="adopcion-sucursal" className="h-10 rounded-none border-[#0A0A0A] w-[220px]"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__" data-testid="adopcion-sucursal-todas">Todas las sucursales</SelectItem>
                  {(consumo?.items || []).map((s) => (
                    <SelectItem key={s.id_sucursal} value={s.id_sucursal} data-testid={`adopcion-sucursal-opt-${s.id_sucursal}`}>{s.nombre}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="flex flex-wrap items-end gap-2 mb-4">
            {[{ k: "hoy", label: "Hoy" }, { k: "7d", label: "Últimos 7 días" }, { k: "30d", label: "Últimos 30 días" }, { k: "rango", label: "Rango personalizado" }].map(({ k, label }) => (
              <button key={k} onClick={() => cambiarAdopPeriodo(k)} data-testid={`adopcion-periodo-${k}`}
                className={`h-10 px-4 border font-bold uppercase tracking-wider text-xs transition-colors duration-200 ${adopPeriodo === k ? "bg-[#0A0A0A] text-white border-[#0A0A0A]" : "border-[#e4e4e7] text-[#52525B] hover:border-[#0A0A0A]"}`}>
                {label}
              </button>
            ))}
            {adopPeriodo === "rango" && (
              <>
                <div className="space-y-1">
                  <Label className="text-[10px] tracking-[0.15em] uppercase font-bold text-[#52525B]">Desde</Label>
                  <Input type="date" value={adopRango.desde} onChange={(e) => setAdopRango({ ...adopRango, desde: e.target.value })} data-testid="adopcion-rango-desde" className="h-10 rounded-none border-[#0A0A0A] w-[150px]" />
                </div>
                <div className="space-y-1">
                  <Label className="text-[10px] tracking-[0.15em] uppercase font-bold text-[#52525B]">Hasta</Label>
                  <Input type="date" value={adopRango.hasta} onChange={(e) => setAdopRango({ ...adopRango, hasta: e.target.value })} data-testid="adopcion-rango-hasta" className="h-10 rounded-none border-[#0A0A0A] w-[150px]" />
                </div>
                <button onClick={aplicarAdopRango} disabled={adopCargando} data-testid="adopcion-aplicar-rango" className="h-10 px-4 border border-[#0A0A0A] font-bold uppercase tracking-wider text-xs inline-flex items-center gap-1.5 hover:bg-[#0A0A0A] hover:text-white transition-colors duration-200 disabled:opacity-60">
                  {adopCargando ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Filter className="h-3.5 w-3.5" />} Aplicar
                </button>
              </>
            )}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-testid="adopcion-cards">
            {[{ id: "MANUAL", nombre: "Manual" }, { id: "VOZ_IA", nombre: "Voz IA" }, { id: "FOTO_IA", nombre: "Fotografía IA" }].map((f) => {
              const cnt = adopcion?.metodos?.[f.id] ?? 0;
              const p = adopcion && adopcion.total > 0 ? ((cnt / adopcion.total) * 100).toFixed(1) : "0.0";
              return (
                <div key={f.id} className="border border-[#0A0A0A] bg-white p-6" data-testid={`adopcion-metodo-${f.id}`}>
                  <p className="text-xs uppercase tracking-[0.15em] font-bold text-[#52525B]">{f.nombre}</p>
                  <p className="font-display text-4xl font-black tracking-tight mt-2" data-testid={`adopcion-pct-${f.id}`}>{adopCargando ? "—" : `${p}%`}</p>
                  <p className="text-sm text-[#52525B] mt-1" data-testid={`adopcion-cnt-${f.id}`}>{adopCargando ? "" : `${Number(cnt).toLocaleString("es-MX")} vehículos`}</p>
                </div>
              );
            })}
          </div>
          <div className="mt-3 border-t-2 border-[#0A0A0A] pt-3 flex items-center justify-between" data-testid="adopcion-total">
            <span className="text-xs uppercase tracking-wider font-bold text-[#52525B]">Total</span>
            <span className="font-display text-lg font-black" data-testid="adopcion-total-valor">{adopcion ? Number(adopcion.total).toLocaleString("es-MX") : "0"} vehículos registrados</span>
          </div>
        </div>

        {/* Sprint 37.1 — Consumo por Sucursal */}
        <div className="mb-10" data-testid="consumo-sucursal-seccion">
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-3">
            <h2 className="font-display text-2xl font-black tracking-tight flex items-center gap-2"><BarChart3 className="h-5 w-5" /> Consumo por Sucursal</h2>
            <div className="flex flex-wrap items-end gap-2">
              <div className="space-y-1">
                <Label className="text-[10px] tracking-[0.15em] uppercase font-bold text-[#52525B]">Desde</Label>
                <Input type="date" value={rango.desde} onChange={(e) => setRango({ ...rango, desde: e.target.value })} data-testid="consumo-rango-desde" className="h-10 rounded-none border-[#0A0A0A] w-[150px]" />
              </div>
              <div className="space-y-1">
                <Label className="text-[10px] tracking-[0.15em] uppercase font-bold text-[#52525B]">Hasta</Label>
                <Input type="date" value={rango.hasta} onChange={(e) => setRango({ ...rango, hasta: e.target.value })} data-testid="consumo-rango-hasta" className="h-10 rounded-none border-[#0A0A0A] w-[150px]" />
              </div>
              <button onClick={aplicarRango} disabled={cargandoRango} data-testid="consumo-aplicar-rango" className="h-10 px-4 border border-[#0A0A0A] font-bold uppercase tracking-wider text-xs inline-flex items-center gap-1.5 hover:bg-[#0A0A0A] hover:text-white transition-colors duration-200 disabled:opacity-60">
                {cargandoRango ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Filter className="h-3.5 w-3.5" />} Aplicar
              </button>
              {consumo?.rango_aplicado && (
                <button onClick={limpiarRango} data-testid="consumo-limpiar-rango" className="h-10 px-4 border border-[#e4e4e7] text-[#52525B] font-bold uppercase tracking-wider text-xs">Limpiar</button>
              )}
            </div>
          </div>
          <div className="bg-white border border-[#e4e4e7] overflow-x-auto">
            <table className="w-full text-sm" data-testid="consumo-tabla">
              <thead><tr className="border-b border-[#e4e4e7]"><th className={th}>Sucursal</th><th className={`${th} text-right`}>Consumo Hoy</th><th className={`${th} text-right`}>Consumo Mes</th>{consumo?.rango_aplicado && <th className={`${th} text-right`}>Consumo Rango</th>}</tr></thead>
              <tbody>
                {!consumo ? <tr><td colSpan={4} className="px-4 py-10 text-center text-[#52525B]"><Loader2 className="h-5 w-5 animate-spin inline" /></td></tr>
                : consumo.items.length === 0 ? <tr><td colSpan={4} className="px-4 py-12 text-center text-[#52525B]" data-testid="consumo-empty">No hay sucursales.</td></tr>
                : consumo.items.map((c) => (
                  <tr key={c.id_sucursal} className="border-b border-[#f0f0f1]" data-testid={`consumo-row-${c.id_sucursal}`}>
                    <td className="px-4 py-3 font-medium">{c.nombre}</td>
                    <td className="px-4 py-3 text-right font-mono" data-testid={`consumo-hoy-${c.id_sucursal}`}>{Number(c.consumo_hoy).toLocaleString("es-MX")}</td>
                    <td className="px-4 py-3 text-right font-mono" data-testid={`consumo-mes-${c.id_sucursal}`}>{Number(c.consumo_mes).toLocaleString("es-MX")}</td>
                    {consumo.rango_aplicado && <td className="px-4 py-3 text-right font-mono">{Number(c.consumo_rango || 0).toLocaleString("es-MX")}</td>}
                  </tr>
                ))}
              </tbody>
              {consumo && consumo.items.length > 0 && (
                <tfoot><tr className="border-t-2 border-[#0A0A0A] bg-[#fafafa] font-bold" data-testid="consumo-total-row">
                  <td className="px-4 py-3 uppercase tracking-wider text-xs">Total empresa</td>
                  <td className="px-4 py-3 text-right font-mono">{Number(consumo.total_hoy).toLocaleString("es-MX")}</td>
                  <td className="px-4 py-3 text-right font-mono">{Number(consumo.total_mes).toLocaleString("es-MX")}</td>
                  {consumo.rango_aplicado && <td className="px-4 py-3 text-right font-mono">{Number(consumo.total_rango || 0).toLocaleString("es-MX")}</td>}
                </tr></tfoot>
              )}
            </table>
          </div>
        </div>

        {/* Catálogo */}
        {loading ? <div className="py-16 text-center"><Loader2 className="h-6 w-6 animate-spin inline" /></div> : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-10" data-testid="paquetes-catalogo">
            {paquetes.length === 0 ? <p className="text-[#52525B]">No hay paquetes disponibles.</p>
            : paquetes.map((p) => (
              <div key={p.id_paquete} className="border border-[#0A0A0A] bg-white p-6 flex flex-col" data-testid={`paquete-card-${p.id_paquete}`}>
                <p className="font-display font-black text-xl tracking-tight">{p.nombre}</p>
                <p className="text-4xl font-display font-black mt-3">{p.cantidad_folios.toLocaleString("es-MX")}<span className="text-sm font-bold text-[#52525B] ml-1">folios</span></p>
                <p className="text-2xl font-mono font-bold mt-2">{fmt(p.precio)}</p>
                <button onClick={() => abrirCompra(p)} data-testid={`comprar-${p.id_paquete}`} className="btn-hard mt-6 h-11 bg-[#FFCC00] text-[#0A0A0A] border border-[#0A0A0A] font-bold uppercase tracking-wider text-sm inline-flex items-center justify-center gap-2"><ShoppingCart className="h-4 w-4" /> Comprar</button>
              </div>
            ))}
          </div>
        )}

        {/* Historial */}
        <h2 className="font-display text-2xl font-black tracking-tight mb-3">Historial de recargas</h2>
        <div className="bg-white border border-[#e4e4e7] overflow-x-auto">
          <table className="w-full text-sm" data-testid="recargas-historial">
            <thead><tr className="border-b border-[#e4e4e7]"><th className={th}>Fecha</th><th className={th}>Paquete</th><th className={`${th} text-right`}>Folios</th><th className={`${th} text-right`}>Monto</th><th className={th}>Método</th><th className={th}>Estado</th><th className={th}>Referencia</th><th className={th}>Factura / CFDI</th></tr></thead>
            <tbody>
              {historial.length === 0 ? <tr><td colSpan={8} className="px-4 py-12 text-center text-[#52525B]" data-testid="recargas-empty">Aún no hay recargas.</td></tr>
              : historial.map((r) => (
                <tr key={r.id_recarga} className="border-b border-[#f0f0f1]" data-testid={`recarga-row-${r.id_recarga}`}>
                  <td className="px-4 py-3 whitespace-nowrap">{fmtFecha(r.fecha_creacion)}</td>
                  <td className="px-4 py-3">{r.paquete_nombre || "—"}</td>
                  <td className="px-4 py-3 text-right font-mono">{r.cantidad_folios.toLocaleString("es-MX")}</td>
                  <td className="px-4 py-3 text-right font-mono font-bold">{fmt(r.monto_pagado)}</td>
                  <td className="px-4 py-3">{r.metodo_pago}</td>
                  <td className="px-4 py-3"><span className={`inline-block text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 border ${ESTADO_STYLE[r.estado] || ""}`} data-testid={`recarga-estado-${r.id_recarga}`}>{r.estado}</span></td>
                  <td className="px-4 py-3 text-[#52525B]">{r.referencia_comprobante || "—"}</td>
                  <td className="px-4 py-3" data-testid={`recarga-cfdi-${r.id_recarga}`}>
                    {r.estado !== "Aprobada" ? (
                      <span className="text-[#a1a1aa]">—</span>
                    ) : !r.cfdi_estado ? (
                      <button onClick={() => solicitarFactura(r)} disabled={facturando === r.id_recarga} data-testid={`solicitar-factura-${r.id_recarga}`}
                        className="h-8 px-3 border border-[#0A0A0A] font-bold uppercase tracking-wider text-[11px] inline-flex items-center gap-1.5 hover:bg-[#0A0A0A] hover:text-white transition-colors duration-200 disabled:opacity-60">
                        {facturando === r.id_recarga ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <FileText className="h-3.5 w-3.5" />} Solicitar Factura
                      </button>
                    ) : r.cfdi_estado === "Pendiente" ? (
                      <span className="inline-block text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 border text-[#B45309] border-[#F59E0B]/40 bg-[#F59E0B]/5" data-testid={`cfdi-badge-${r.id_recarga}`}>En proceso</span>
                    ) : r.cfdi_estado === "Timbrado" ? (
                      <div className="flex items-center gap-2" data-testid={`cfdi-descargas-${r.id_recarga}`}>
                        <button onClick={() => descargarCfdi(r.cfdi_id, "pdf")} disabled={descargando === `${r.cfdi_id}-pdf`} data-testid={`descargar-pdf-${r.id_recarga}`} title="Descargar PDF"
                          className="h-8 px-2.5 border border-[#0A0A0A] font-bold uppercase tracking-wider text-[11px] inline-flex items-center gap-1 hover:bg-[#0A0A0A] hover:text-white transition-colors duration-200 disabled:opacity-60">
                          {descargando === `${r.cfdi_id}-pdf` ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />} PDF
                        </button>
                        <button onClick={() => descargarCfdi(r.cfdi_id, "xml")} disabled={descargando === `${r.cfdi_id}-xml`} data-testid={`descargar-xml-${r.id_recarga}`} title="Descargar XML"
                          className="h-8 px-2.5 border border-[#0A0A0A] font-bold uppercase tracking-wider text-[11px] inline-flex items-center gap-1 hover:bg-[#0A0A0A] hover:text-white transition-colors duration-200 disabled:opacity-60">
                          {descargando === `${r.cfdi_id}-xml` ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />} XML
                        </button>
                      </div>
                    ) : r.cfdi_estado === "Error" ? (
                      <div className="flex items-center gap-2" data-testid={`cfdi-error-${r.id_recarga}`}>
                        <span className="inline-block text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 border text-[#B91C1C] border-[#EF4444]/40 bg-[#EF4444]/5">Error</span>
                        <button onClick={() => setMotivoModal(r.cfdi_error || "Sin motivo especificado.")} data-testid={`ver-motivo-${r.id_recarga}`}
                          className="text-[11px] font-bold uppercase tracking-wider underline underline-offset-2 hover:text-[#B91C1C]">Ver motivo</button>
                        <button onClick={() => reintentarFactura(r)} disabled={reintentando === r.cfdi_id} data-testid={`reintentar-${r.id_recarga}`}
                          className="h-8 px-2.5 border border-[#0A0A0A] font-bold uppercase tracking-wider text-[11px] inline-flex items-center gap-1 hover:bg-[#0A0A0A] hover:text-white transition-colors duration-200 disabled:opacity-60">
                          {reintentando === r.cfdi_id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RotateCcw className="h-3.5 w-3.5" />} Reintentar
                        </button>
                      </div>
                    ) : (
                      <span className="inline-block text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 border text-[#047857] border-[#10B981]/40 bg-[#10B981]/5" data-testid={`cfdi-badge-${r.id_recarga}`}>{r.cfdi_estado}</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </motion.div>

      {/* Modal de compra */}
      <Dialog open={compra !== null} onOpenChange={(o) => { if (!o) setCompra(null); }}>
        <DialogContent className="rounded-none border-[#0A0A0A]" data-testid="compra-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-xl font-black tracking-tight">Comprar folios</DialogTitle>
            <DialogDescription>{compra?.paquete?.nombre}</DialogDescription>
          </DialogHeader>
          {compra && (
            <div className="space-y-4 py-2">
              <div className="border border-[#e4e4e7] p-4 flex justify-between items-center">
                <div><p className="text-xs uppercase tracking-wider font-bold text-[#52525B]">Folios</p><p className="font-display font-black text-2xl">{compra.paquete.cantidad_folios.toLocaleString("es-MX")}</p></div>
                <div className="text-right"><p className="text-xs uppercase tracking-wider font-bold text-[#52525B]">Total</p><p className="font-mono font-black text-2xl">{fmt(compra.paquete.precio)}</p></div>
              </div>
              <div className="space-y-2"><Label className="text-xs tracking-[0.15em] uppercase font-bold text-[#52525B]">Método de pago</Label>
                <Select value={compra.metodo} onValueChange={(v) => setCompra({ ...compra, metodo: v })}>
                  <SelectTrigger data-testid="compra-metodo" className={fld}><SelectValue placeholder="Selecciona método" /></SelectTrigger>
                  <SelectContent>{METODOS.map((m) => <SelectItem key={m} value={m} data-testid={`metodo-${m}`}>{m}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              {esManual && (
                <div className="space-y-2"><Label className="text-xs tracking-[0.15em] uppercase font-bold text-[#52525B]">Referencia del comprobante *</Label>
                  <Input value={compra.referencia} onChange={(e) => setCompra({ ...compra, referencia: e.target.value })} placeholder="Ej. folio de transferencia / recibo" data-testid="compra-referencia" className={fld} />
                </div>
              )}
              {compra.metodo === "Tarjeta" && (
                <div className="border border-[#3B82F6]/40 bg-[#3B82F6]/5 p-3 text-sm text-[#1D4ED8] flex items-start gap-2" data-testid="compra-mp-msg">
                  <CreditCard className="h-4 w-4 mt-0.5 shrink-0" />
                  <span>Serás redirigido a <b>Mercado Pago</b> para completar el pago con tarjeta. Al confirmarse, los folios se <b>acreditarán automáticamente</b> a tu saldo global.</span>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <button onClick={() => setCompra(null)} className="h-11 px-5 border border-[#0A0A0A] font-bold uppercase tracking-wider text-sm">Cancelar</button>
            <button onClick={confirmar} disabled={saving} data-testid="compra-confirmar" className="btn-hard h-11 px-5 bg-[#FFCC00] text-[#0A0A0A] border border-[#0A0A0A] font-bold uppercase tracking-wider text-sm inline-flex items-center gap-2 disabled:opacity-60">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Confirmar compra"}</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal Ver motivo del error */}
      <Dialog open={motivoModal !== null} onOpenChange={(o) => { if (!o) setMotivoModal(null); }}>
        <DialogContent className="rounded-none border-[#0A0A0A]" data-testid="motivo-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-xl font-black tracking-tight flex items-center gap-2"><AlertTriangle className="h-5 w-5 text-[#B91C1C]" /> Motivo del error</DialogTitle>
            <DialogDescription>El SuperAdmin no pudo timbrar tu factura por lo siguiente. Corrige tus Datos Fiscales si aplica y usa "Reintentar".</DialogDescription>
          </DialogHeader>
          <div className="border border-[#EF4444]/40 bg-[#EF4444]/5 p-4 text-sm text-[#B91C1C]" data-testid="motivo-texto">{motivoModal}</div>
          <DialogFooter>
            <button onClick={() => setMotivoModal(null)} className="h-11 px-5 border border-[#0A0A0A] font-bold uppercase tracking-wider text-sm">Cerrar</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
